import { apiSlice } from "application/api/apiSlice";
import { API_PREFIX } from "data/constants";
import { isUuid } from "data/uuid";

export const holidayPackageSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getHolidayPackage: builder.query({
      query: (id) => ({
        url:
          API_PREFIX +
          (isUuid(id)
            ? `/holiday_package/single/${id}`
            : `/holiday_package/single/by-slug/${id}`),
        method: "GET",
      }),
      transformResponse: (response) => ({
        holidayPackage: {
          ...response.holidayPackage,
        },
        linkedProperties: {
          ...response.holidayPackage.linkedProperties,
        },
        linkedExperiences: {
          ...response.holidayPackage.linkedExperiences,
        },
        host: {
          ...response.holidayPackage.host,
        },
      }),
      providesTags: ["holiday_package", "EditingHolidayPackage"],
    }),
    getHolidayPackageStats: builder.query({
      query: (id) => ({
        url: API_PREFIX + `/holiday_package/single/stats/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => ({
        ...response.stats,
      }),
      providesTags: ["holiday_package", "EditingHolidayPackage"],
    }),
    updateHolidayPackage: builder.mutation({
      query: (holidayPackage) => ({
        url: API_PREFIX + "/holiday_package/single",
        method: "PATCH",
        body: holidayPackage,
      }),
      invalidatesTags: ["holiday_package"],
    }),
    createHolidayPackage: builder.mutation({
      query: (holidayPackage) => ({
        url: API_PREFIX + "/holiday_package/single",
        method: "PUT",
        body: holidayPackage,
      }),
      invalidatesTags: ["holiday_package"],
    }),
    uploadHolidayPackageCover: builder.mutation({
      query: (formData) => ({
        url: API_PREFIX + "/holiday_package/upload/cover",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["holiday_package", "EditingHolidayPackage"],
    }),
    uploadHolidayPackageGalleryImage: builder.mutation({
      query: (formData) => ({
        url: API_PREFIX + "/holiday_package/upload/gallery_image",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["holiday_package", "EditingHolidayPackage"],
    }),
    getAllHolidayPackage: builder.query({
      query: () => ({
        url: API_PREFIX + "/holiday_package/all",
        method: "GET",
      }),
    }),
    editHolidayPackage: builder.mutation({
      query: (holidayPackage) => ({
        url: API_PREFIX + "/holiday_package/single",
        method: "PATCH",
        body: holidayPackage,
      }),
      invalidatesTags: ["EditingHolidayPackage"],
    }),
    duplicateHolidayPackage: builder.mutation({
      query: (package_uuid) => ({
        url: API_PREFIX + `/holiday_package/duplicate/${package_uuid}`,
        method: "POST",
      }),
      invalidatesTags: ["holiday_package"],
    }),
    getFilterHolidayPackages: builder.query({
      query: (query) => ({
        url: API_PREFIX + "/holiday_package/filter",
        method: "POST",
        body: {
          check_in: query.check_in,
          check_out: query.check_out,
          guests: query.guests,
          mood: query.mood,
          latitude: query.latitude,
          longitude: query.longitude,
          bboxNorth: query.bboxNorth,
          bboxSouth: query.bboxSouth,
          bboxEast: query.bboxEast,
          bboxWest: query.bboxWest,
          region: query.region,
          state: query.country,
          trend: query.trend,
        },
      }),
    }),
    getHolidayPackageAvailabilityByDates: builder.query({
      query: (availability) => ({
        url: API_PREFIX + "/holiday_package/single/availability",
        method: "POST",
        body: availability,
      }),
    }),
    getHolidayPackageAvailabilityByRange: builder.query({
      query: (availability) => ({
        url: API_PREFIX + "/holiday_package/single/availability/range",
        method: "POST",
        body: availability,
      }),
    }),
    stopHolidayPackage: builder.query({
      query: (holidayPackage_uuid) => ({
        url: API_PREFIX + `/holiday_package/single/stop/${holidayPackage_uuid}`,
        method: "POST",
        body: holidayPackage_uuid,
      }),
    }),
    getHolidayPackageByHost: builder.query({
      query: (host_uuid) => ({
        url: API_PREFIX + `/holiday_package/host/${host_uuid}`,
        method: "GET",
      }),
    }),
    getAveragePrice: builder.query({
      query: (city) => ({
        url: API_PREFIX + `/holiday_package/averagePrice/${city}`,
        method: "GET",
      }),
    }),
    getHolidayPackageHighlights: builder.query({
      query: () => ({
        url: API_PREFIX + `/holiday_package/highlights/`,
        method: "GET",
      }),
    }),
    getHolidayPackageByCity: builder.query({
      query: (filter) => ({
        url: API_PREFIX + `/holiday_package/byCity/`,
        method: "POST",
        body: filter,
      }),
    }),
    deleteHolidayPackage: builder.mutation({
      query: (holidayPackage_uuid) => ({
        url: API_PREFIX + `/holiday_package/single/${holidayPackage_uuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EditingHolidayPackage"],
    }),
    sendPackageRequest: builder.mutation({
      query: (requestData) => ({
        url: API_PREFIX + "/email/support",
        method: "POST",
        body: requestData,
      }),
    }),
  }),
});

export const {
  useGetHolidayPackageQuery,
  useLazyGetHolidayPackageQuery,
  useUpdateHolidayPackageMutation,
  useCreateHolidayPackageMutation,
  useUploadHolidayPackageCoverMutation,
  useUploadHolidayPackageGalleryImageMutation,
  useDuplicateHolidayPackageMutation,
  useGetAllHolidayPackageQuery,
  useEditHolidayPackageMutation,
  useGetFilterHolidayPackagesQuery,
  useLazyGetFilterHolidayPackagesQuery,
  useLazyGetHolidayPackageStatsQuery,
  useGetHolidayPackageStatsQuery,
  useGetHolidayPackageAvailabilityByDatesQuery,
  useLazyGetHolidayPackageAvailabilityByDatesQuery,
  useLazyGetHolidayPackageAvailabilityByRangeQuery,
  useLazyGetAllHolidayPackageQuery,
  useGetHolidayPackageByHostQuery,
  useLazyGetHolidayPackageByHostQuery,
  useLazyGetAveragePriceQuery,
  useGetHolidayPackageHighlightsQuery,
  useGetHolidayPackageByCityQuery,
  useLazyGetHolidayPackageByCityQuery,
  useDeleteHolidayPackageMutation,
  useSendPackageRequestMutation,
} = holidayPackageSlice;
